// src/components/Header.js

import React, { useState, useEffect, useRef } from 'react';
import {
  FiUser,
  FiLogOut,
  FiLock,
  FiChevronDown,
  FiMoon,
  FiSun,
  FiX,
  FiMapPin,
  FiMenu,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const SESSION_CHECK_INTERVAL = 30000;
const SESSION_WARNING_TIME = 300000;

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch { return true; }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch { return 0; }
};

const Header = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(0);

  const [userData, setUserData] = useState({
    name: '', email: '', role: '', department: '',
    staffId: '', phone: '', location: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '', newPassword: '', confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });

  const navigate = useNavigate();
  const menuRef = useRef(null);
  const dialogRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const passwordDialogRef = useRef(null);

  const refreshAuthToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) throw new Error('No refresh token available');
      const response = await axios.post(`${FULL_API_URL}/auth/refresh`, {
        refresh_token: refreshToken,
      });
      if (response.data && response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        return response.data.access_token;
      }
      throw new Error('No access token in refresh response');
    } catch (err) {
      console.error('[Header] Token refresh failed:', err);
      localStorage.clear();
      navigate('/restaurantAuth');
      return null;
    }
  };

  useEffect(() => {
    const name = localStorage.getItem('userName') || localStorage.getItem('first_name') || 'User';
    const email = localStorage.getItem('user_email') || localStorage.getItem('email') || '';
    const role = localStorage.getItem('user_role') || localStorage.getItem('role') || 'Staff';
    const department = localStorage.getItem('department') || localStorage.getItem('userDepartment') || '';
    const staffId = localStorage.getItem('staff_id') || localStorage.getItem('staffId') || '';
    const phone = localStorage.getItem('phone') || localStorage.getItem('phoneNumber') || '';
    const location = localStorage.getItem('user_location') || localStorage.getItem('location') || '';
    const formattedLocation = location ? location.charAt(0).toUpperCase() + location.slice(1) : '';

    setUserData({
      name, email,
      role: role.charAt(0).toUpperCase() + role.slice(1),
      department, staffId, phone,
      location: formattedLocation,
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const checkSession = () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setSessionWarning(false);
        setTimeUntilExpiry(0);
        return;
      }
      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();
      setTimeUntilExpiry(timeLeft);

      if (timeLeft <= 0) {
        refreshAuthToken().then(newToken => {
          if (!newToken) {
            localStorage.clear();
            navigate('/restaurantAuth');
          }
        });
      } else if (timeLeft < SESSION_WARNING_TIME && timeLeft > 0) {
        setSessionWarning(true);
      } else {
        setSessionWarning(false);
      }
    };
    checkSession();
    const interval = setInterval(checkSession, SESSION_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    const autoRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();
      if (timeLeft > 0 && timeLeft < 120000) {
        await refreshAuthToken();
      }
    };
    const refreshInterval = setInterval(autoRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showLogoutDialog || showMobileMenu || showChangePasswordDialog) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [showLogoutDialog, showMobileMenu, showChangePasswordDialog]);

  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const handleLogout = () => {
    setShowLogoutDialog(true);
    setShowProfileMenu(false);
    setShowMobileMenu(false);
  };

  const confirmLogout = () => {
    localStorage.clear();
    navigate('/restaurantAuth');
    setShowLogoutDialog(false);
    setShowMobileMenu(false);
  };

  const cancelLogout = () => setShowLogoutDialog(false);

  const handleChangePasswordClick = () => {
    setShowChangePasswordDialog(true);
    setShowProfileMenu(false);
    setShowMobileMenu(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordErrors({});
    setPasswordSuccess(false);
    setPasswordStrength({ score: 0, label: '', color: '' });
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'newPassword' && value) setPasswordStrength(checkPasswordStrength(value));
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    const strengths = [
      { score: 0, label: 'Very Weak', color: '#F44336' },
      { score: 1, label: 'Weak', color: '#FF9800' },
      { score: 2, label: 'Fair', color: '#FFC107' },
      { score: 3, label: 'Good', color: '#8BC34A' },
      { score: 4, label: 'Strong', color: '#4CAF50' },
      { score: 5, label: 'Very Strong', color: '#2E7D32' },
    ];
    return strengths.find(s => s.score === score) || strengths[0];
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordData.currentPassword) errors.currentPassword = 'Current password is required';
    if (!passwordData.newPassword) errors.newPassword = 'New password is required';
    else if (passwordData.newPassword.length < 8) errors.newPassword = 'Password must be at least 8 characters';
    else if (!/[a-z]/.test(passwordData.newPassword) || !/[A-Z]/.test(passwordData.newPassword))
      errors.newPassword = 'Password must contain both uppercase and lowercase letters';
    else if (!/\d/.test(passwordData.newPassword)) errors.newPassword = 'Password must contain at least one number';
    if (!passwordData.confirmPassword) errors.confirmPassword = 'Please confirm your password';
    else if (passwordData.newPassword !== passwordData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (passwordData.currentPassword && passwordData.newPassword &&
        passwordData.currentPassword === passwordData.newPassword)
      errors.newPassword = 'New password must be different from current password';
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) return;
    setIsChangingPassword(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${FULL_API_URL}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_new_password: passwordData.confirmPassword,
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      setPasswordSuccess(true);
      setTimeout(() => {
        setShowChangePasswordDialog(false);
        setPasswordSuccess(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }, 2000);
    } catch (err) {
      setPasswordErrors({
        general: err.response?.data?.detail || err.message || 'Failed to change password. Please try again.',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getRoleColor = (role) => {
    const roleColors = {
      'Admin': '#C62828', 'Manager': '#E65100', 'Waiter': '#2E7D32',
      'Cashier': '#1565C0', 'Staff': '#6A1B9A', 'HR': '#00838F',
      'Account': '#BF360C', 'Store Keeper': '#4E342E',
    };
    return roleColors[role] || '#666';
  };

  const getLocationColor = (location) => {
    const locationColors = { 'Continental': '#1565C0', 'African': '#E65100', 'Lounge': '#6A1B9A' };
    return locationColors[location] || '#666';
  };

  const userInitial = userData.name ? userData.name.charAt(0).toUpperCase() : 'U';
  const isWaiter = userData.role?.toLowerCase() === 'waiter';
  const isManager = userData.role?.toLowerCase() === 'manager';
  const showLocation = (isWaiter || isManager) && userData.location;

  const formatTimeUntilExpiry = (ms) => {
    if (ms <= 0) return 'Expired';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  return (
    <>
      <header className="epos-header lounge-header">
        {sessionWarning && timeUntilExpiry > 0 && timeUntilExpiry < SESSION_WARNING_TIME && (
          <div className="session-warning-banner">
            <span>⚠️ Session expires in {formatTimeUntilExpiry(timeUntilExpiry)}</span>
            <button onClick={() => refreshAuthToken()} className="session-renew-btn">Renew Session</button>
          </div>
        )}

        <button
          className="mobile-menu-toggle"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label="Toggle menu"
        >
          <FiMenu size={22} />
        </button>

        {/* Lounge brand mark */}
        <div className="lounge-brand">
          <span className="brand-ornament">✦</span>
          <span className="brand-text">Lounge</span>
        </div>

        {/* Center clock + F&B label */}
        <div className="header-center">
          <div className="digital-clock">
            <span className="clock-icon">✦</span>
            <span className="clock-time">{formatTime(currentTime)}</span>
            <span className="clock-divider">·</span>
            <span className="clock-date">{formatDate(currentTime)}</span>
          </div>
          <div className="fnb-label">
            <span className="fnb-dot"></span>
            <span className="fnb-text">F&amp;B</span>
          </div>
        </div>

        <div className="header-right">
          {showLocation && (
            <div className="location-badge">
              <FiMapPin size={14} className="location-icon" />
              <span className="location-text">{userData.location}</span>
            </div>
          )}

          <div className="profile-wrapper desktop-only" ref={menuRef}>
            <div className="profile" onClick={() => setShowProfileMenu(!showProfileMenu)}>
              <div className="avatar-wrapper">
                <div className="avatar">{userInitial}</div>
                <div className="avatar-status online"></div>
              </div>
              <div className="profile-info">
                <div className="profile-name">{userData.name}</div>
                <div className="profile-role">
                  <span className="role-badge" style={{ backgroundColor: getRoleColor(userData.role) }}>
                    {userData.role}
                  </span>
                  {(isWaiter || isManager) && userData.location && (
                    <span className="location-badge-small" style={{ backgroundColor: getLocationColor(userData.location) }}>
                      <FiMapPin size={11} />
                      {userData.location}
                    </span>
                  )}
                </div>
              </div>
              <FiChevronDown size={16} className="profile-arrow" />
            </div>

            {showProfileMenu && (
              <div className="profile-dropdown">
                <div className="dropdown-user-info">
                  <div className="dropdown-avatar">{userInitial}</div>
                  <div>
                    <div className="dropdown-name">{userData.name}</div>
                    <div className="dropdown-email">{userData.email}</div>
                  </div>
                </div>

                {userData.department && (
                  <div className="dropdown-badge"><FiUser size={15} /><span>{userData.department}</span></div>
                )}
                {(isWaiter || isManager) && userData.location && (
                  <div className="dropdown-badge"><FiMapPin size={15} /><span>Location: {userData.location}</span></div>
                )}
                {userData.staffId && (
                  <div className="dropdown-badge"><FiUser size={15} /><span>ID: {userData.staffId}</span></div>
                )}

                <div className="dropdown-divider"></div>
                <div className="dropdown-item" onClick={handleChangePasswordClick}>
                  <FiLock size={17} /><span>Change Password</span>
                </div>
                <div className="dropdown-divider"></div>
                <div className="dropdown-item logout" onClick={handleLogout}>
                  <FiLogOut size={17} /><span>Logout</span>
                </div>
              </div>
            )}
          </div>

          <div className="mobile-avatar" onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <div className="avatar small">{userInitial}</div>
          </div>
        </div>
      </header>

      {showMobileMenu && (
        <>
          <div className="mobile-overlay" onClick={() => setShowMobileMenu(false)}></div>
          <div className="mobile-menu" ref={mobileMenuRef}>
            <div className="mobile-user-info">
              <div className="mobile-user-avatar">{userInitial}</div>
              <div className="mobile-user-details">
                <div className="mobile-user-name">{userData.name}</div>
                <div className="mobile-user-role">{userData.role}</div>
                {(isWaiter || isManager) && userData.location && (
                  <div className="mobile-user-location">
                    <FiMapPin size={13} /><span>{userData.location}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="mobile-menu-divider"></div>

            <div className="mobile-menu-items">
              <button className="mobile-menu-item" onClick={handleChangePasswordClick}>
                <FiLock size={19} /><span>Change Password</span>
              </button>
              <button className="mobile-menu-item" onClick={() => setIsDarkMode(!isDarkMode)}>
                {isDarkMode ? <FiSun size={19} /> : <FiMoon size={19} />}
                <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
              </button>
            </div>

            <div className="mobile-menu-divider"></div>

            <button className="mobile-logout-btn" onClick={handleLogout}>
              <FiLogOut size={19} /><span>Logout</span>
            </button>
          </div>
        </>
      )}

      {/* Logout Dialog */}
      {showLogoutDialog && (
        <div className="dialog-overlay" onClick={cancelLogout}>
          <div className="dialog-container" ref={dialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-content">
              <div className="dialog-icon-wrapper">
                <div className="dialog-icon"><FiLogOut size={34} /></div>
              </div>
              <h3 className="dialog-title">Confirm Logout</h3>
              <p className="dialog-message">
                Are you sure you want to logout? You will need to login again to access your account.
              </p>
              <div className="dialog-user-info">
                <div className="dialog-user-avatar">{userInitial}</div>
                <div className="dialog-user-details">
                  <div className="dialog-user-name">{userData.name}</div>
                  <div className="dialog-user-email">{userData.email}</div>
                  {(isWaiter || isManager) && userData.location && (
                    <div className="dialog-user-location">
                      <FiMapPin size={13} /><span>{userData.location}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="dialog-actions">
                <button className="dialog-btn dialog-btn-cancel" onClick={cancelLogout}>Cancel</button>
                <button className="dialog-btn dialog-btn-confirm" onClick={confirmLogout}>
                  <FiLogOut size={17} />Logout
                </button>
              </div>
            </div>
            <button className="dialog-close" onClick={cancelLogout}><FiX size={22} /></button>
          </div>
        </div>
      )}

      {/* Change Password Dialog */}
      {showChangePasswordDialog && (
        <div className="dialog-overlay" onClick={() => setShowChangePasswordDialog(false)}>
          <div className="dialog-container password-dialog" ref={passwordDialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-content">
              <div className="dialog-icon-wrapper password-icon-wrapper">
                <div className="dialog-icon password-icon"><FiLock size={30} /></div>
              </div>
              <h3 className="dialog-title">Change Password</h3>
              <p className="dialog-message">
                Enter your current password and choose a new password to update your account security.
              </p>

              {passwordErrors.general && (
                <div className="password-error-general">
                  <FiAlertCircle size={15} /><span>{passwordErrors.general}</span>
                </div>
              )}
              {passwordSuccess && (
                <div className="password-success">
                  <FiCheckCircle size={17} /><span>Password changed successfully! Redirecting...</span>
                </div>
              )}

              <div className="password-form">
                <div className="password-field">
                  <label className="password-label">Current Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      className={`password-input ${passwordErrors.currentPassword ? 'error' : ''}`}
                      placeholder="Enter current password"
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                      disabled={passwordSuccess}
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                      {showCurrentPassword ? <FiEyeOff size={17} /> : <FiEye size={17} />}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && <span className="password-error">{passwordErrors.currentPassword}</span>}
                </div>

                <div className="password-field">
                  <label className="password-label">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      className={`password-input ${passwordErrors.newPassword ? 'error' : ''}`}
                      placeholder="Enter new password (min. 8 characters)"
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      disabled={passwordSuccess}
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowNewPassword(!showNewPassword)}>
                      {showNewPassword ? <FiEyeOff size={17} /> : <FiEye size={17} />}
                    </button>
                  </div>
                  {passwordErrors.newPassword && <span className="password-error">{passwordErrors.newPassword}</span>}
                  {passwordData.newPassword && !passwordErrors.newPassword && (
                    <div className="password-strength">
                      <div className="strength-bar-container">
                        <div className="strength-bar" style={{
                          width: `${(passwordStrength.score / 5) * 100}%`,
                          backgroundColor: passwordStrength.color,
                        }} />
                      </div>
                      <span className="strength-label" style={{ color: passwordStrength.color }}>
                        {passwordStrength.label}
                      </span>
                    </div>
                  )}
                </div>

                <div className="password-field">
                  <label className="password-label">Confirm New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`password-input ${passwordErrors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm new password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      disabled={passwordSuccess}
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? <FiEyeOff size={17} /> : <FiEye size={17} />}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && <span className="password-error">{passwordErrors.confirmPassword}</span>}
                </div>

                <div className="password-requirements">
                  <div className={`requirement ${passwordData.newPassword.length >= 8 ? 'met' : ''}`}>
                    <span className="requirement-icon">{passwordData.newPassword.length >= 8 ? '✓' : '○'}</span>
                    <span>At least 8 characters</span>
                  </div>
                  <div className={`requirement ${/[a-z]/.test(passwordData.newPassword) && /[A-Z]/.test(passwordData.newPassword) ? 'met' : ''}`}>
                    <span className="requirement-icon">
                      {/[a-z]/.test(passwordData.newPassword) && /[A-Z]/.test(passwordData.newPassword) ? '✓' : '○'}
                    </span>
                    <span>Uppercase &amp; lowercase letters</span>
                  </div>
                  <div className={`requirement ${/\d/.test(passwordData.newPassword) ? 'met' : ''}`}>
                    <span className="requirement-icon">{/\d/.test(passwordData.newPassword) ? '✓' : '○'}</span>
                    <span>At least one number</span>
                  </div>
                </div>
              </div>

              <div className="dialog-actions">
                <button
                  className="dialog-btn dialog-btn-cancel"
                  onClick={() => {
                    setShowChangePasswordDialog(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordErrors({});
                    setPasswordSuccess(false);
                  }}
                  disabled={isChangingPassword}
                >Cancel</button>
                <button
                  className="dialog-btn dialog-btn-confirm password-confirm-btn"
                  onClick={handleChangePassword}
                  disabled={isChangingPassword || passwordSuccess}
                >
                  {isChangingPassword ? <span className="loading-spinner" /> : <FiLock size={15} />}
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
            <button className="dialog-close" onClick={() => {
              setShowChangePasswordDialog(false);
              setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              setPasswordErrors({});
              setPasswordSuccess(false);
            }}><FiX size={22} /></button>
          </div>
        </div>
      )}

      <style>{`
        /* ============ ELEGANT WHITE + MILD RED HEADER ============ */
        .lounge-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          padding: 12px 26px;
          min-height: 72px;
          background:
            linear-gradient(180deg, #ffffff 0%, #fdfcfc 60%, #faf8f8 100%);
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          position: sticky;
          top: 0;
          z-index: 100;
          flex-wrap: wrap;
          font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03), 0 8px 24px rgba(0, 0, 0, 0.03);
        }

        /* Elegant red accent line at the bottom border */
        .lounge-header::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 2px;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(198, 40, 40, 0.15) 8%,
            rgba(198, 40, 40, 0.75) 30%,
            #C62828 50%,
            rgba(198, 40, 40, 0.75) 70%,
            rgba(198, 40, 40, 0.15) 92%,
            transparent 100%
          );
          pointer-events: none;
        }

        /* Brand mark */
        .lounge-brand {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 6px 16px 6px 13px;
          border-radius: 999px;
          background: linear-gradient(135deg, #ffffff, #fdf5f5);
          border: 1px solid rgba(198, 40, 40, 0.18);
          box-shadow:
            0 1px 2px rgba(198, 40, 40, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
          flex-shrink: 0;
        }

        .brand-ornament {
          font-size: 14px;
          color: #C62828;
          text-shadow: 0 0 8px rgba(198, 40, 40, 0.3);
        }

        .brand-text {
          font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: 2.4px;
          background: linear-gradient(135deg, #8B0000 0%, #C62828 50%, #8B0000 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* Mobile toggle */
        .mobile-menu-toggle {
          display: none;
          background: #ffffff;
          border: 1px solid rgba(198, 40, 40, 0.2);
          color: #C62828;
          cursor: pointer;
          padding: 7px;
          border-radius: 10px;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        .mobile-menu-toggle:hover {
          background: #fdf5f5;
          border-color: rgba(198, 40, 40, 0.45);
          box-shadow: 0 2px 10px rgba(198, 40, 40, 0.1);
        }

        /* Center clock */
        .header-center {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-shrink: 0;
          margin-left: auto;
          margin-right: auto;
        }

        .digital-clock {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 7px 20px;
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid rgba(198, 40, 40, 0.16);
          box-shadow:
            0 1px 2px rgba(198, 40, 40, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }

        .clock-icon {
          color: #C62828;
          font-size: 12px;
        }

        .clock-time {
          font-family: 'Courier New', monospace;
          font-size: 17px;
          font-weight: 700;
          color: #1a1a1a;
          letter-spacing: 1.2px;
        }

        .clock-divider {
          color: rgba(198, 40, 40, 0.35);
          font-size: 14px;
        }

        .clock-date {
          font-size: 12px;
          font-weight: 500;
          color: #6b6b6b;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .fnb-label {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px 6px 13px;
          border-radius: 999px;
          background: linear-gradient(135deg, #ffffff, #fdf5f5);
          border: 1px solid rgba(198, 40, 40, 0.2);
          color: #8B0000;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
          cursor: default;
        }

        .fnb-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #C62828;
          box-shadow: 0 0 8px rgba(198, 40, 40, 0.55);
          animation: fnbPulse 2.4s ease-in-out infinite;
        }
        @keyframes fnbPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }

        /* Right side */
        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }

        .location-badge {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 6px 14px 6px 11px;
          border-radius: 999px;
          background: #fdf5f8;
          border: 1px solid rgba(106, 27, 154, 0.2);
          color: #4a1259;
          font-size: 12.5px;
          font-weight: 600;
          letter-spacing: 0.4px;
        }
        .location-icon { color: #6A1B9A; }

        /* Profile */
        .profile-wrapper { position: relative; }

        .profile {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 5px 14px 5px 5px;
          border-radius: 999px;
          cursor: pointer;
          background: #ffffff;
          border: 1px solid rgba(198, 40, 40, 0.14);
          transition: all 0.3s ease;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }

        .profile:hover {
          border-color: rgba(198, 40, 40, 0.4);
          box-shadow:
            0 4px 16px rgba(198, 40, 40, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
          transform: translateY(-1px);
        }

        .avatar-wrapper { position: relative; flex-shrink: 0; }

        .avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C62828 0%, #8B0000 100%);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 15px;
          letter-spacing: 0.5px;
          box-shadow:
            0 3px 12px rgba(198, 40, 40, 0.28),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.4);
        }

        .avatar.small { width: 34px; height: 34px; font-size: 13px; }

        .avatar-status {
          position: absolute;
          bottom: 1px;
          right: 1px;
          width: 11px;
          height: 11px;
          border-radius: 50%;
          border: 2px solid #ffffff;
        }
        .avatar-status.online {
          background: #4caf50;
          box-shadow: 0 0 8px rgba(76, 175, 80, 0.6);
        }

        .profile-info { display: flex; flex-direction: column; line-height: 1.3; }

        .profile-name {
          font-size: 13.5px;
          font-weight: 700;
          color: #1a1a1a;
          letter-spacing: 0.2px;
        }

        .profile-role {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }

        .role-badge {
          font-size: 9.5px;
          font-weight: 800;
          padding: 2px 9px;
          border-radius: 6px;
          color: #fff;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .location-badge-small {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 2px 8px 2px 6px;
          border-radius: 12px;
          font-size: 9.5px;
          font-weight: 700;
          color: #fff;
          margin-left: 4px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        .profile-arrow {
          color: #999;
          transition: transform 0.3s ease, color 0.3s ease;
          flex-shrink: 0;
        }
        .profile:hover .profile-arrow { transform: rotate(180deg); color: #C62828; }

        /* Profile Dropdown — light with red accents */
        .profile-dropdown {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          width: 268px;
          background: #ffffff;
          border: 1px solid rgba(198, 40, 40, 0.18);
          border-radius: 16px;
          box-shadow:
            0 16px 40px rgba(0, 0, 0, 0.1),
            0 4px 12px rgba(198, 40, 40, 0.06);
          overflow: hidden;
          z-index: 1000;
          animation: slideDown 0.22s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .dropdown-user-info {
          padding: 16px 18px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid rgba(198, 40, 40, 0.1);
          background: linear-gradient(180deg, #fdf7f7, #ffffff);
        }

        .dropdown-avatar {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C62828 0%, #8B0000 100%);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 17px;
          flex-shrink: 0;
          box-shadow: 0 3px 12px rgba(198, 40, 40, 0.25);
          border: 1px solid rgba(255, 255, 255, 0.4);
        }

        .dropdown-name {
          font-size: 14.5px;
          font-weight: 700;
          color: #1a1a1a;
          letter-spacing: 0.2px;
        }
        .dropdown-email {
          font-size: 11.5px;
          font-weight: 500;
          color: #777;
        }

        .dropdown-badge {
          padding: 8px 18px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 12.5px;
          font-weight: 500;
          color: #333;
          margin: 2px 10px;
          border-radius: 8px;
          background: #fafafa;
        }
        .dropdown-badge svg { color: #C62828; }

        .dropdown-divider {
          height: 1px;
          background: rgba(198, 40, 40, 0.1);
          margin: 4px 0;
        }

        .dropdown-item {
          padding: 11px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13.5px;
          font-weight: 500;
          color: #1a1a1a;
        }
        .dropdown-item svg { color: #C62828; }
        .dropdown-item:hover {
          background: #fdf5f5;
          color: #8B0000;
        }

        .dropdown-item.logout {
          color: #C62828;
          font-weight: 600;
        }
        .dropdown-item.logout svg { color: #C62828; }
        .dropdown-item.logout:hover {
          background: #fdeeee;
          color: #8B0000;
        }

        /* Mobile avatar */
        .mobile-avatar { display: none; cursor: pointer; }

        /* Session banner — mild red */
        .session-warning-banner {
          width: 100%;
          background: linear-gradient(135deg, #fdf5f5, #fbecec);
          border: 1px solid rgba(198, 40, 40, 0.25);
          padding: 8px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 13px;
          font-weight: 600;
          color: #8B0000;
          border-radius: 10px;
          margin-bottom: 8px;
          animation: pulseWarning 2s ease-in-out infinite;
        }
        @keyframes pulseWarning {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .session-renew-btn {
          background: linear-gradient(135deg, #C62828, #8B0000);
          color: #ffffff;
          border: none;
          padding: 5px 18px;
          border-radius: 8px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 12px;
          letter-spacing: 0.4px;
          box-shadow: 0 2px 10px rgba(198, 40, 40, 0.3);
        }
        .session-renew-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 16px rgba(198, 40, 40, 0.4);
        }

        /* Mobile Menu — white with red accents */
        .mobile-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.35);
          backdrop-filter: blur(6px);
          z-index: 998;
          animation: fadeIn 0.3s ease;
        }

        .mobile-menu {
          position: fixed;
          top: 0;
          right: 0;
          width: 320px;
          max-width: 85vw;
          height: 100vh;
          background: #ffffff;
          border-left: 1px solid rgba(198, 40, 40, 0.15);
          z-index: 999;
          padding: 24px 20px;
          overflow-y: auto;
          animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: -10px 0 40px rgba(0, 0, 0, 0.15);
        }

        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .mobile-user-info {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 6px 0 20px;
        }

        .mobile-user-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C62828 0%, #8B0000 100%);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 21px;
          flex-shrink: 0;
          box-shadow: 0 4px 16px rgba(198, 40, 40, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.4);
        }

        .mobile-user-name {
          font-size: 17px;
          font-weight: 700;
          color: #1a1a1a;
          letter-spacing: 0.3px;
        }
        .mobile-user-role {
          font-size: 12.5px;
          font-weight: 500;
          color: #777;
        }
        .mobile-user-location {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12.5px;
          font-weight: 500;
          color: #6A1B9A;
          margin-top: 3px;
        }

        .mobile-menu-divider {
          height: 1px;
          background: rgba(198, 40, 40, 0.1);
          margin: 14px 0;
        }

        .mobile-menu-items {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .mobile-menu-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 13px 14px;
          border: none;
          background: transparent;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14.5px;
          font-weight: 500;
          color: #1a1a1a;
          width: 100%;
          text-align: left;
        }
        .mobile-menu-item svg { color: #C62828; }
        .mobile-menu-item:hover {
          background: #fdf5f5;
          color: #8B0000;
        }

        .mobile-logout-btn {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px;
          border: 1px solid rgba(198, 40, 40, 0.25);
          background: #fdf5f5;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14.5px;
          font-weight: 600;
          color: #C62828;
          width: 100%;
          text-align: left;
        }
        .mobile-logout-btn:hover { background: #fdeeee; }

        /* Dialogs */
        .dialog-overlay {
          position: fixed;
          inset: 0;
          background: rgba(20, 5, 5, 0.55);
          backdrop-filter: blur(10px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s ease;
        }

        .dialog-container {
          background: #ffffff;
          border: 1px solid rgba(198, 40, 40, 0.18);
          border-radius: 22px;
          padding: 34px 28px 26px;
          max-width: 400px;
          width: 90%;
          position: relative;
          box-shadow:
            0 24px 60px rgba(0, 0, 0, 0.2),
            0 4px 12px rgba(198, 40, 40, 0.06);
          animation: scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .password-dialog { max-width: 440px; }

        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .dialog-close {
          position: absolute;
          top: 14px;
          right: 14px;
          background: #fafafa;
          border: 1px solid rgba(198, 40, 40, 0.15);
          color: #777;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        .dialog-close:hover {
          background: #fdf5f5;
          color: #C62828;
          border-color: rgba(198, 40, 40, 0.35);
        }

        .dialog-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .dialog-icon-wrapper { margin-bottom: 16px; }
        .password-icon-wrapper { margin-bottom: 12px; }

        .dialog-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #fdf5f5;
          color: #C62828;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(198, 40, 40, 0.2);
        }

        .password-icon {
          background: #eef4fd;
          color: #1565C0;
          border-color: rgba(21, 101, 192, 0.2);
        }

        .dialog-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 21px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 8px 0;
          letter-spacing: 0.5px;
        }

        .dialog-message {
          font-size: 13.5px;
          font-weight: 500;
          color: #6b6b6b;
          line-height: 1.6;
          margin: 0 0 20px 0;
        }

        .dialog-user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #fafafa;
          border: 1px solid rgba(198, 40, 40, 0.1);
          border-radius: 12px;
          width: 100%;
          margin-bottom: 22px;
        }

        .dialog-user-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C62828 0%, #8B0000 100%);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 15px;
          flex-shrink: 0;
        }

        .dialog-user-details { text-align: left; flex: 1; }
        .dialog-user-name {
          font-size: 14.5px;
          font-weight: 700;
          color: #1a1a1a;
        }
        .dialog-user-email {
          font-size: 11.5px;
          font-weight: 500;
          color: #777;
        }
        .dialog-user-location {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11.5px;
          font-weight: 500;
          color: #6A1B9A;
          margin-top: 2px;
        }

        .dialog-actions {
          display: flex;
          gap: 12px;
          width: 100%;
        }

        .dialog-btn {
          flex: 1;
          padding: 12px 20px;
          border: none;
          border-radius: 12px;
          font-size: 13.5px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          letter-spacing: 0.3px;
        }
        .dialog-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .dialog-btn-cancel {
          background: #f5f5f5;
          color: #555;
          border: 1px solid rgba(0, 0, 0, 0.06);
        }
        .dialog-btn-cancel:hover:not(:disabled) {
          background: #ededed;
          color: #1a1a1a;
        }

        .dialog-btn-confirm {
          background: linear-gradient(135deg, #C62828 0%, #8B0000 100%);
          color: #ffffff;
          box-shadow: 0 4px 16px rgba(198, 40, 40, 0.3);
        }
        .dialog-btn-confirm:hover:not(:disabled) {
          box-shadow: 0 8px 24px rgba(198, 40, 40, 0.4);
          transform: translateY(-1px);
        }

        .password-confirm-btn {
          background: linear-gradient(135deg, #1565C0, #0D47A1);
          box-shadow: 0 4px 16px rgba(21, 101, 192, 0.3);
        }
        .password-confirm-btn:hover:not(:disabled) {
          box-shadow: 0 8px 24px rgba(21, 101, 192, 0.4);
        }

        /* Password Form */
        .password-form { width: 100%; text-align: left; margin-bottom: 4px; }

        .password-field { margin-bottom: 16px; }

        .password-label {
          display: block;
          font-size: 12.5px;
          font-weight: 600;
          color: #333;
          margin-bottom: 6px;
        }

        .password-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-input {
          width: 100%;
          padding: 10px 40px 10px 14px;
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 10px;
          font-size: 13.5px;
          transition: all 0.3s ease;
          background: #fafafa;
          color: #1a1a1a;
        }
        .password-input::placeholder { color: #aaa; }
        .password-input:focus {
          outline: none;
          border-color: rgba(198, 40, 40, 0.6);
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(198, 40, 40, 0.08);
        }
        .password-input.error { border-color: rgba(198, 40, 40, 0.7); }
        .password-input.error:focus {
          box-shadow: 0 0 0 4px rgba(198, 40, 40, 0.1);
        }
        .password-input:disabled { opacity: 0.6; cursor: not-allowed; }

        .password-toggle {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: #999;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.3s ease;
        }
        .password-toggle:hover { color: #C62828; }

        .password-error {
          display: block;
          font-size: 11.5px;
          color: #C62828;
          margin-top: 4px;
        }

        .password-error-general {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #fdf5f5;
          border: 1px solid rgba(198, 40, 40, 0.25);
          border-radius: 10px;
          color: #8B0000;
          font-size: 12.5px;
          width: 100%;
          margin-bottom: 16px;
        }

        .password-success {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #f1faf1;
          border: 1px solid rgba(76, 175, 80, 0.3);
          border-radius: 10px;
          color: #2E7D32;
          font-size: 12.5px;
          width: 100%;
          margin-bottom: 16px;
          animation: successPulse 1.5s ease;
        }
        @keyframes successPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        .password-strength {
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .strength-bar-container {
          flex: 1;
          height: 4px;
          background: #eee;
          border-radius: 4px;
          overflow: hidden;
        }
        .strength-bar {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease, background-color 0.3s ease;
        }
        .strength-label {
          font-size: 11px;
          font-weight: 600;
          min-width: 60px;
          text-align: right;
        }

        .password-requirements {
          margin-top: 12px;
          padding: 12px 14px;
          background: #fafafa;
          border: 1px solid rgba(0, 0, 0, 0.05);
          border-radius: 10px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
        }

        .requirement {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11.5px;
          color: #999;
          transition: color 0.3s ease;
        }
        .requirement.met { color: #2E7D32; }

        .requirement-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          font-size: 10px;
          font-weight: 700;
          color: #999;
          border: 1.5px solid #ddd;
          transition: all 0.3s ease;
        }
        .requirement.met .requirement-icon {
          background: #4caf50;
          color: #fff;
          border-color: #4caf50;
        }

        .loading-spinner {
          display: inline-block;
          width: 17px;
          height: 17px;
          border: 2px solid rgba(255, 255, 255, 0.4);
          border-top: 2px solid #ffffff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .lounge-header { padding: 10px 18px; }
          .clock-date { display: none; }
          .brand-text { font-size: 16px; letter-spacing: 2px; }
        }

        @media (max-width: 768px) {
          .lounge-header {
            padding: 8px 14px;
            min-height: 60px;
            gap: 10px;
          }
          .mobile-menu-toggle {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .header-center { display: none; }
          .desktop-only { display: none !important; }
          .mobile-avatar { display: block; }
          .location-badge { display: none; }
          .header-right { gap: 8px; }
          .brand-text { font-size: 15px; letter-spacing: 1.8px; }
          .dialog-container { padding: 28px 20px 20px; max-width: 360px; }
          .password-dialog { max-width: 360px; }
          .password-requirements { grid-template-columns: 1fr; }
          .dialog-actions { flex-direction: column; }
          .session-warning-banner {
            font-size: 12px;
            padding: 6px 12px;
            flex-wrap: wrap;
            gap: 8px;
          }
          .session-renew-btn { font-size: 11px; padding: 3px 12px; }
        }

        @media (max-width: 480px) {
          .lounge-header { padding: 6px 10px; min-height: 54px; }
          .mobile-menu { width: 300px; padding: 20px 16px; }
          .mobile-user-avatar { width: 48px; height: 48px; font-size: 18px; }
          .mobile-user-name { font-size: 15.5px; }
          .avatar { width: 32px; height: 32px; font-size: 13px; }
          .avatar.small { width: 30px; height: 30px; font-size: 12px; }
          .dialog-container { padding: 24px 16px 16px; max-width: 320px; }
          .password-dialog { max-width: 320px; }
          .dialog-title { font-size: 18px; }
          .dialog-message { font-size: 12.5px; }
          .brand-text { font-size: 13px; letter-spacing: 1.5px; }
          .brand-ornament { font-size: 12px; }
          .session-warning-banner { font-size: 11px; padding: 4px 10px; }
          .session-renew-btn { font-size: 10px; padding: 2px 10px; }
        }
      `}</style>
    </>
  );
};

export default Header;